/**
 * Google Cloud Storage Integration for Photo Management
 *
 * File Storage Structure:
 * coral-gardeners/{branchId}/images/nurseries/{nurseryId}/before_after/
 *
 * Filename Format:
 * {systemTimestamp}_{userTimestamp}_{tag}_{type}_{originalFilename}
 *
 * Where:
 * - systemTimestamp: Unix timestamp when file was uploaded (Date.now())
 * - userTimestamp: User-selected date converted to Unix timestamp
 * - tag: Category/label for the image (spaces replaced with underscores)
 * - type: Either 'preview' (800px width) or 'source' (original file)
 * - originalFilename: Original name of the uploaded file
 *
 * Example:
 * 1683900000000_1683813600000_coral_growth_preview_photo1.jpg
 *
 * Storage Features:
 * - Dual timestamp system for accurate record-keeping
 * - Preview images are resized to 800px width for faster loading
 * - Original source files are preserved
 * - Images are organized by branch, nursery, and tags
 * - Filenames maintain both system and user timestamps
 *
 * Display/Sorting:
 * - UI displays and sorts by userTimestamp (when photo was taken)
 * - Falls back to systemTimestamp if userTimestamp is unavailable
 * - Maintains audit trail through systemTimestamp
 */

import {
  getStorage,
  ref as storageRef,
  listAll,
  getDownloadURL,
  deleteObject,
  uploadBytes,
  uploadBytesResumable,
} from "firebase/storage";

export class ImageManager {
  constructor(branchId, nurseryId) {
    this.branchId = branchId;
    this.nurseryId = nurseryId;
    this._images = [];
    this.loading = false;
    this.error = null;
    this.uploading = false;
  }

  async fetchImages(tagId) {
    if (!tagId) return [];

    this.loading = true;
    this.error = null;
    try {
      this._images = await fetchImagesFromGCS(
        this.branchId,
        this.nurseryId,
        tagId
      );
      return this._images;
    } catch (error) {
      console.error("Error fetching images:", error);
      this.error = "Failed to load images";
      this._images = [];
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async deleteImage(image) {
    try {
      await deleteImageFromGCS(this.branchId, this.nurseryId, image);
      // Remove the image from the local array
      this._images = this._images.filter((img) => img.id !== image.id);
      return true;
    } catch (error) {
      console.error("Error deleting image:", error);
      throw error;
    }
  }

  async uploadImage(file, tag) {
    try {
      this.uploading = true;
      const newImage = await uploadImageToGCS(
        this.branchId,
        this.nurseryId,
        tag,
        file
      );
      this._images.unshift(newImage);
      return newImage;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    } finally {
      this.uploading = false;
    }
  }

  getImages() {
    return this._images;
  }

  set images(newImages) {
    this._images = newImages;
  }

  isLoading() {
    return this.loading;
  }

  isUploading() {
    return this.uploading;
  }

  getError() {
    return this.error;
  }

  clearError() {
    this.error = null;
  }
}

export class TagManager {
  constructor(branchId, nurseryId) {
    this.branchId = branchId;
    this.nurseryId = nurseryId;
    this.tags = [];
    this.selectedTag = null;
  }

  async fetchTags() {
    try {
      const fetchedTags = await fetchTagsFromGCS(this.branchId, this.nurseryId);
      this.tags = fetchedTags || [];
      return this.tags;
    } catch (error) {
      console.error("Error fetching tags:", error);
      this.tags = [];
      throw error;
    }
  }

  getTagOptions() {
    if (!Array.isArray(this.tags)) return [];
    return this.tags.map((tag) => ({
      value: tag.id,
      label: tag.label,
      storageId: tag.storageId,
    }));
  }

  setSelectedTag(tagId) {
    if (this.selectedTag === tagId) return false;
    this.selectedTag = tagId;
    return true;
  }

  getSelectedTag() {
    return this.selectedTag;
  }

  hasSelectedTag() {
    return Boolean(this.selectedTag);
  }

  getInitialTag() {
    return this.tags.length > 0 ? this.tags[0].id : null;
  }

  getTags() {
    return this.tags;
  }

  async deleteTag(tagId) {
    try {
      // First, get all images with this tag
      const images = await fetchImagesFromGCS(
        this.branchId,
        this.nurseryId,
        tagId
      );

      // Delete all images associated with the tag
      for (const image of images) {
        await deleteImageFromGCS(this.branchId, this.nurseryId, image);
      }

      // Remove the tag from local state
      this.tags = this.tags.filter((tag) => tag.id !== tagId);

      // Reset selected tag if it was deleted
      if (this.selectedTag === tagId) {
        this.selectedTag = this.getInitialTag();
      }

      // Return the next selected tag (if any)
      return {
        success: true,
        nextTag: this.selectedTag,
      };
    } catch (error) {
      console.error("Error deleting tag:", error);
      throw new Error("Failed to delete tag and its associated images");
    }
  }

  static validateTagFormat(tag) {
    return validateTag(tag);
  }

  static formatForDisplay(tag) {
    return formatTagForDisplay(tag);
  }

  static formatForStorage(tag) {
    return formatTagForStorage(tag);
  }
}

export const fetchImagesFromGCS = async (branchId, nurseryId, selectedTag) => {
  try {
    const storage = getStorage();
    const basePath = `coral-gardeners/${branchId}/images/nurseries/${nurseryId}/before_after`;
    const imagesRef = storageRef(storage, basePath);

    // List all items in the directory
    const result = await listAll(imagesRef);

    // Updated regex for parsing both timestamps
    const imageRegex =
      /^([0-9]+)_([0-9]+)_([^_]+(?:_[^_]+)*)_(preview|source)_(.+)$/;

    const imagePromises = result.items
      .filter((item) => {
        const match = item.name.match(imageRegex);
        if (!match) return false;
        const tagFromFile = match[3];
        return tagFromFile === selectedTag && match[4] === "preview";
      })
      .map(async (item) => {
        try {
          const match = item.name.match(imageRegex);
          if (!match) return null;
          const [, timestamp, userTimestamp, tagId, type, originalName] = match;
          const previewUrl = await getDownloadURL(item);

          // Get source URL
          let sourceUrl = null;
          try {
            const sourceRef = storageRef(
              storage,
              `${basePath}/${timestamp}_${userTimestamp}_${tagId}_source_${originalName}`
            );
            sourceUrl = await getDownloadURL(sourceRef);
          } catch {
            sourceUrl = previewUrl;
          }

          return {
            id: item.name,
            url: previewUrl,
            sourceUrl,
            originalName,
            timestamp: parseInt(timestamp),
            userTimestamp: parseInt(userTimestamp),
            tag: tagId.replace(/_/g, " "),
          };
        } catch (itemError) {
          return null;
        }
      });

    // Filter out any null results and sort by userTimestamp
    const results = await Promise.all(imagePromises);
    return results
      .filter((item) => item !== null)
      .sort((a, b) => b.userTimestamp - a.userTimestamp);
  } catch (error) {
    console.error("Error fetching images:", error);
    throw error;
  }
};

export const deleteImageFromGCS = async (branchId, nurseryId, image) => {
  try {
    const storage = getStorage();
    const basePath = `coral-gardeners/${branchId}/images/nurseries/${nurseryId}/before_after`;

    // Delete preview image
    const previewRef = storageRef(storage, `${basePath}/${image.id}`);
    await deleteObject(previewRef);

    // Try to delete source image if it exists
    try {
      const sourceFileName = image.id.replace("_preview_", "_source_");
      const sourceRef = storageRef(storage, `${basePath}/${sourceFileName}`);
      await deleteObject(sourceRef);
    } catch (error) {
      console.warn("Source image not found or already deleted:", error);
    }

    return true;
  } catch (error) {
    console.error("Error deleting image from GCS:", error);
    throw error;
  }
};

export const checkDuplicateTimestamp = async (
  branchId,
  nurseryId,
  tag,
  userTimestamp
) => {
  try {
    const existingImages = await fetchImagesFromGCS(branchId, nurseryId, tag);
    return existingImages.some((img) => img.userTimestamp === userTimestamp);
  } catch (error) {
    console.error("Error checking for duplicate timestamp:", error);
    throw error;
  }
};

export const uploadImageToGCS = async (
  branchId,
  nurseryId,
  tag,
  file,
  onProgress,
  userTimestamp = null
) => {
  try {
    const actualTimestamp = userTimestamp || Date.now();

    // Check for duplicate timestamp
    const hasDuplicate = await checkDuplicateTimestamp(
      branchId,
      nurseryId,
      tag,
      actualTimestamp
    );
    if (hasDuplicate) {
      throw new Error(
        "An image already exists for this date. Please choose a different date."
      );
    }

    console.log("Starting upload process for:", file.name);
    const storage = getStorage();
    const basePath = `coral-gardeners/${branchId}/images/nurseries/${nurseryId}/before_after`;
    const timestamp = Date.now(); // System timestamp

    // Create filename with both timestamps
    const filename = `${timestamp}_${actualTimestamp}_${tag}`;

    // Create canvas for resizing
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Load the original image
    const img = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = URL.createObjectURL(file);
    });
    console.log("Image loaded and ready for resizing");

    // Calculate new dimensions (maintaining aspect ratio)
    const MAX_WIDTH = 800;
    const scale = MAX_WIDTH / img.width;
    const newWidth = img.width * scale;
    const newHeight = img.height * scale;

    // Set canvas size and draw resized image
    canvas.width = newWidth;
    canvas.height = newHeight;
    ctx.drawImage(img, 0, 0, newWidth, newHeight);
    console.log("Image resized:", { newWidth, newHeight });

    // Convert canvas to blob
    const previewBlob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.8);
    });
    console.log("Preview blob created");

    // Create references for both files
    const sourceRef = storageRef(
      storage,
      `${basePath}/${filename}_source_${file.name}`
    );
    const previewRef = storageRef(
      storage,
      `${basePath}/${filename}_preview_${file.name}`
    );
    console.log("Storage references created", {
      sourcePath: sourceRef.fullPath,
      previewPath: previewRef.fullPath,
    });

    // Create upload tasks
    const sourceTask = uploadBytesResumable(sourceRef, file);
    const previewTask = uploadBytesResumable(previewRef, previewBlob);

    let sourceProgress = 0;
    let previewProgress = 0;

    // Monitor source upload progress
    sourceTask.on(
      "state_changed",
      (snapshot) => {
        sourceProgress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Source upload progress:", {
          sourceProgress,
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes,
        });
        if (onProgress) {
          const progressData = {
            sourceProgress,
            previewProgress,
            totalProgress: (sourceProgress + previewProgress) / 2,
            state: "uploading",
          };
          console.log("Progress callback data:", progressData);
          onProgress(progressData);
        }
      },
      (error) => {
        console.error("Source upload error:", error);
        if (onProgress) {
          onProgress({
            sourceProgress,
            previewProgress,
            totalProgress: (sourceProgress + previewProgress) / 2,
            state: "error",
            error,
          });
        }
        throw error;
      }
    );

    // Monitor preview upload progress
    previewTask.on(
      "state_changed",
      (snapshot) => {
        previewProgress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Preview upload progress:", {
          previewProgress,
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes,
        });
        if (onProgress) {
          const progressData = {
            sourceProgress,
            previewProgress,
            totalProgress: (sourceProgress + previewProgress) / 2,
            state: "uploading",
          };
          console.log("Progress callback data:", progressData);
          onProgress(progressData);
        }
      },
      (error) => {
        console.error("Preview upload error:", error);
        if (onProgress) {
          onProgress({
            sourceProgress,
            previewProgress,
            totalProgress: (sourceProgress + previewProgress) / 2,
            state: "error",
            error,
          });
        }
        throw error;
      }
    );

    console.log("Waiting for uploads to complete...");
    // Wait for both uploads to complete
    await Promise.all([sourceTask, previewTask]);
    console.log("Both uploads completed successfully");

    // Get download URLs
    const [sourceUrl, previewUrl] = await Promise.all([
      getDownloadURL(sourceRef),
      getDownloadURL(previewRef),
    ]);
    console.log("Download URLs obtained");

    // Signal completion
    if (onProgress) {
      const finalProgress = {
        sourceProgress: 100,
        previewProgress: 100,
        totalProgress: 100,
        state: "complete",
      };
      console.log("Final progress callback:", finalProgress);
      onProgress(finalProgress);
    }

    return {
      id: `${filename}_preview_${file.name}`,
      url: previewUrl,
      sourceUrl: sourceUrl,
      originalName: file.name,
      timestamp: timestamp,
      userTimestamp: actualTimestamp,
      tag: tag.replace(/_/g, " "),
    };
  } catch (error) {
    console.error("Error in uploadImageToGCS:", error);
    if (onProgress) {
      onProgress({
        sourceProgress: 0,
        previewProgress: 0,
        totalProgress: 0,
        state: "error",
        error,
      });
    }
    throw error;
  }
};

export const fetchTagsFromGCS = async (branchId, nurseryId) => {
  try {
    const storage = getStorage();
    const basePath = `coral-gardeners/${branchId}/images/nurseries/${nurseryId}/before_after`;
    const imagesRef = storageRef(storage, basePath);

    const result = await listAll(imagesRef);
    // Updated regex to handle spaces in tags
    const imageRegex = /^[0-9]+_[0-9]+_(.+?)_(preview|source)_/;

    // Extract unique tags from filenames
    const tagSet = new Set();
    result.items.forEach((item) => {
      const match = item.name.match(imageRegex);
      if (match) {
        const rawTag = match[1];
        // Convert storage format back to display format
        const originalTag = rawTag.replace(/_/g, " ").trim();
        tagSet.add(originalTag);
      }
    });

    // Convert to array and map to objects, preserving original format
    return Array.from(tagSet).map((tag) => ({
      id: tag, // Keep original format for ID
      label: tag, // Keep original format for label
      storageId: formatTagForStorage(tag), // Add storageId for internal use
    }));
  } catch (error) {
    console.error("Error fetching tags:", error);
    throw error;
  }
};

export const validateTag = (tag) => {
  // Only allow letters, numbers, spaces, and basic punctuation
  // Disallow characters that could cause issues with storage paths
  if (!tag || typeof tag !== "string") return false;

  // Disallow storage-unsafe characters: /\?%*:|"<>
  const unsafeChars = /[/\\?%*:|"<>]/;
  if (unsafeChars.test(tag)) return false;

  // Ensure tag isn't empty after trimming
  const trimmed = tag.trim();
  if (trimmed.length === 0) return false;

  // Maximum length check (reasonable limit for filenames)
  if (trimmed.length > 100) return false;

  return true;
};

export const formatTagForDisplay = (tag) => {
  return tag || ""; // Simply return the tag as is, or empty string if null/undefined
};

export const formatTagForStorage = (tag) => {
  if (!tag) return "";
  // Replace unsafe characters with underscores for storage
  // but keep the original case and spaces
  return tag
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "_") // Replace unsafe chars with underscore
    .replace(/\s+/g, " ") // Normalize multiple spaces to single space
    .replace(/^_+|_+$/g, ""); // Remove leading/trailing underscores
};
